import React from 'react';

/*
 * Fit by cooling topology — a qualification grid, not a capability list.
 *
 * The previous version of this section listed generators, chillers, cooling
 * towers and substations as four equal targets. Three of those were wrong in a
 * way that invites exactly the enquiries we cannot serve:
 *
 *   - Cooling towers were described as a fit. Their blade-pass sits BELOW the
 *     band we treat and below what most ordinances measure, splash noise masks
 *     it, and the only real target is gearbox mesh — which has to be confirmed
 *     at the compliance point before anyone quotes.
 *   - Substations were listed as a fit. Transformer hum is a separate product
 *     with its own scope, not this one.
 *   - Generators were listed as the headline fit. They are treatable, but with
 *     conventional critical-grade exhaust silencers — a generator exhaust
 *     tolerates back-pressure a condenser fan does not, which is what rules our
 *     units out and those in. Separate scope; it can still sit inside the SLA.
 *
 * Worse, the two topologies that are our best technical fit AND our largest
 * deployments — dry and adiabatic dry coolers — were missing entirely. Both
 * follow from the same fact: when air does the heat rejection instead of water,
 * the blade-pass tone lands in our band and there are many more fans per
 * megawatt to treat.
 *
 * Tone bands here are problem-domain description — where the difficulty lives —
 * not performance claims. Fan densities are deliberately absent: they are
 * quoting inputs, and they belong in the assessment conversation.
 *
 * Drift is named on the adiabatic card because that topology is the clearest
 * case of it (spray hours are few, so most of the year the fans run backed
 * off and the tone sits somewhere else). It is true of every fan on the grid;
 * the silencer section carries the general answer, this card just points at it.
 */

const TOPOLOGIES = [
  {
    n: '01',
    title: 'Dry coolers',
    band: '50–150 Hz',
    fit: 'good',
    body: 'Blade-pass lands squarely in the band that carries, and air-side rejection means more fans per megawatt than any other plant. Our strongest fit.',
  },
  {
    n: '02',
    title: 'Adiabatic dry coolers',
    band: '50–150 Hz',
    fit: 'good',
    body: 'Same band. The spray runs only a few hundred hours a year. The rest of the time the fans back off and the tone slides across the band, which is drift a fixed silencer cannot hold and ours is built to follow.',
  },
  {
    n: '03',
    title: 'Air-cooled chillers',
    band: '70–170 Hz',
    fit: 'good',
    body: 'Rows of condenser fans running through the night, when limits are strictest and complaints are made. Blade-pass sits in band.',
  },
  {
    n: '04',
    title: 'Cooling towers',
    band: '15–60 Hz',
    fit: 'conditional',
    body: 'One large slow fan does the work of dozens of small ones, so blade-pass falls below the band, and below what most ordinances measure. There is often still a target in the gearbox mesh, but it has to be measured before anyone quotes.',
  },
];

const FIT_LABEL = { good: 'Good fit', conditional: 'Conditional' };

function WhereItFits() {
  return (
    <section className="section fits" id="fits">
      <div className="wrap">
        <p className="eyebrow eyebrow--on-dark">Where it fits</p>
        <h2 className="h2 fits-title">The equipment causing the complaints.</h2>
        <p className="lead fits-lead">
          How your heat gets rejected decides the tone, and the tone decides
          whether we have anything worth selling you. We would rather say so on
          a website than in month three.
        </p>

        <div className="fits-grid">
          {TOPOLOGIES.map((t) => (
            <div className="fits-card" key={t.n}>
              <div className="fits-card-head">
                <span className="fits-card-n">{t.n}</span>
                <span className={`fits-badge fits-badge--${t.fit}`}>
                  {FIT_LABEL[t.fit]}
                </span>
              </div>
              <h3 className="h3 fits-card-title">{t.title}</h3>
              <p className="fits-card-band">{t.band}</p>
              <p className="fits-card-body">{t.body}</p>
            </div>
          ))}
        </div>

        <p className="fits-note">
          <strong className="fits-note-lead">Everything else is a question, not a no.</strong>{' '}
          Generators and transformers are treatable with different equipment:
          critical-grade exhaust silencing and transformer-specific treatment.
          Both are quoted as separate scope, and both can sit inside the same
          SLA. Water-cooled plant rooms, evaporative and hybrid coolers and
          mixed estates each move both the tone and the fan count. We never
          quote one from a category name; we re-derive it from your
          measurements.
        </p>
      </div>
    </section>
  );
}

export default WhereItFits;
